import EventEmitter from 'eventemitter3';
import { DefaultVideoConstraints, Settings } from '../../Settings';
import { safeRetrieve, safeStore } from '../../utils/storage';
let instance;
const Gate = {};
export class MediaStreamManager extends EventEmitter {
    _mediaStream = new MediaStream([]);
    _devices = {
        audio: [],
        audioById: {},
        video: [],
        videoById: {},
    };
    _permissions = {
        audio: false,
        video: false,
    };
    _devicePresence = {
        audio: false,
        video: false,
    };
    userTracks = {
        audio: undefined,
        video: undefined,
    };
    displaySurface;
    displayTracks = {
        audio: undefined,
        video: undefined,
    };
    engageDisplaySurfaceAbortController;
    engageDevicesAbortController;
    _defaultAudioDeviceId;
    _defaultVideoDeviceId;
    _selectedAudioDeviceId = safeRetrieve('audioDeviceId');
    _selectedVideoDeviceId = safeRetrieve('videoDeviceId');
    constructor(gate) {
        if (gate !== Gate) {
            /**
             * Since we only want a single instance of this class let's prevent creating them from
             * the outside.
             */
            throw new Error('Use MediaStreamManager.instance');
        }
        super();
        try {
            /**
             * `navigator.mediaDevices` may be undefined. If it happens, let's skip watching for
             * device list changes.
             */
            navigator.mediaDevices.addEventListener('devicechange', this.updateDeviceList.bind(this));
        }
        catch { }
        void (async () => {
            const permissions = {
                audio: false,
                video: false,
            };
            try {
                await this.getDevicePresenceSnapshot();
            }
            catch { }
            try {
                for (const device of await navigator.mediaDevices.enumerateDevices()) {
                    if (!device.label) {
                        continue;
                    }
                    if (!permissions.audio && device.kind === 'audioinput') {
                        permissions.audio = true;
                    }
                    if (!permissions.video && device.kind === 'videoinput') {
                        permissions.video = true;
                    }
                }
            }
            catch (_) { }
            if (permissions.audio === this._permissions.audio &&
                permissions.video === this._permissions.video) {
                return;
            }
            this.requestPermission(permissions);
        })();
    }
    /**
     * Checks if device selection changes and, if so, engages tracks associated with new
     * device ids. It also then triggers the `selectedDevicesChange` event.
     */
    maybeEngageSelectedDevices(previousIds) {
        if (previousIds.audio !== this.selectedAudioDeviceId ||
            previousIds.video !== this.selectedVideoDeviceId) {
            this.emit('selectedDevicesChange');
            void (async () => {
                try {
                    await this.engageDevices({
                        audio: this.selectedAudioDeviceId || false,
                        video: this.selectedVideoDeviceId || false,
                    });
                }
                catch { }
            })();
        }
    }
    /**
     * Updates the internal record of audio/video devices and their id-to-device LUTs.
     */
    async updateDeviceList() {
        const previousSelectedDeviceIds = {
            audio: this.selectedAudioDeviceId,
            video: this.selectedVideoDeviceId,
        };
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const newDevices = {
                audio: [],
                audioById: {},
                video: [],
                videoById: {},
            };
            for (const device of devices) {
                if (device.kind === 'audioinput') {
                    if (device.deviceId) {
                        newDevices.audio.push(device);
                        newDevices.audioById[device.deviceId] = device;
                    }
                }
                if (device.kind === 'videoinput') {
                    if (device.deviceId) {
                        newDevices.video.push(device);
                        newDevices.videoById[device.deviceId] = device;
                    }
                }
            }
            this._devices = newDevices;
            this.emit('deviceListChange');
            this.maybeEngageSelectedDevices(previousSelectedDeviceIds);
        }
        catch { }
    }
    /**
     * Last known record of media device presence.
     */
    get devicePresence() {
        return this._devicePresence;
    }
    /**
     * An id of the default (and reachable) audio device. Gives `undefined` if the device is no
     * longer available.
     */
    get defaultAudioDeviceId() {
        const { _defaultAudioDeviceId: id } = this;
        return id && this._devices.audioById[id] ? id : undefined;
    }
    /**
     * An id of the default (and reachable) video device. Gives `undefined` if the device is no
     * longer available.
     */
    get defaultVideoDeviceId() {
        const { _defaultVideoDeviceId: id } = this;
        return id && this._devices.videoById[id] ? id : undefined;
    }
    /**
     * An id of the selected audio device.
     *
     * Returns the default audio device id if the explicitly selected one is unavailable.
     */
    get selectedAudioDeviceId() {
        const { _selectedAudioDeviceId: id } = this;
        return id && this._devices.audioById[id]
            ? id
            : this.defaultAudioDeviceId;
    }
    /**
     * An id of the selected video device.
     *
     * Returns the default video device id if the explicitly selected one is unavailable.
     */
    get selectedVideoDeviceId() {
        const { _selectedVideoDeviceId: id } = this;
        return id && this._devices.videoById[id]
            ? id
            : this.defaultVideoDeviceId;
    }
    /**
     * The one and only instance of `MediaStreamManager`.
     */
    static get instance() {
        if (!instance) {
            instance = new MediaStreamManager(Gate);
        }
        return instance;
    }
    /**
     * Current media stream.
     */
    get mediaStream() {
        return this._mediaStream;
    }
    /**
     * Current permissions.
     */
    get permissions() {
        return this._permissions;
    }
    /**
     * Updates the local presence record and triggers the `devicePresenceChange` if the
     * environment changed.
     */
    async getDevicePresenceSnapshot() {
        const presence = {
            audio: false,
            video: false,
        };
        try {
            const devices = await window.navigator.mediaDevices.enumerateDevices();
            for (const device of devices) {
                if (device.kind === 'audioinput') {
                    presence.audio = true;
                }
                if (device.kind === 'videoinput') {
                    presence.video = true;
                }
            }
        }
        catch { }
        if (this._devicePresence.audio !== presence.audio ||
            this._devicePresence.video !== presence.video) {
            this._devicePresence = presence;
            this.emit('devicePresenceChange', presence);
        }
        return presence;
    }
    /**
     * Requests media device permissions. Triggers the `permissionsChange` event if necessary.
     */
    async requestPermission({ audio, video }) {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio,
            video: video && DefaultVideoConstraints,
        });
        if (audio) {
            this._defaultAudioDeviceId = stream
                .getAudioTracks()[0]
                .getSettings().deviceId;
        }
        if (video) {
            this._defaultVideoDeviceId = stream
                .getVideoTracks()[0]
                .getSettings().deviceId;
        }
        await this.updateDeviceList();
        for (const track of stream.getTracks()) {
            track.stop();
        }
        const previousPermissions = {
            ...this.permissions,
        };
        this._permissions.audio = previousPermissions.audio || audio;
        this._permissions.video = previousPermissions.video || video;
        if (this._permissions.audio === previousPermissions.audio &&
            this._permissions.video === previousPermissions.video) {
            return;
        }
        this.emit('permissionsChange', {
            ...this._permissions,
        });
    }
    /**
     * Starts screen sharing, or ends it if given `undefined` value.
     */
    async engageDisplaySurface(value) {
        if (value === this.displaySurface) {
            return;
        }
        if (this.engageDisplaySurfaceAbortController) {
            this.engageDisplaySurfaceAbortController.abort();
            this.engageDisplaySurfaceAbortController = undefined;
        }
        this.displayTracks.audio?.stop();
        this.displayTracks.video?.stop();
        if (!value) {
            this.displayTracks = {
                audio: undefined,
                video: undefined,
            };
            this.displaySurface = undefined;
            this.emit('displaySurfaceChange', undefined);
            this.maybeUpdateMediaStream();
            return;
        }
        const abortController = new AbortController();
        this.engageDisplaySurfaceAbortController = abortController;
        const { signal } = abortController;
        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: value,
                frameRate: {
                    ideal: Settings.videoEncoderDefaultFrameRate,
                },
            },
            audio: {
                sampleRate: Settings.audioSampleRate,
                suppressLocalAudioPlayback: true,
            },
        });
        if (signal.aborted) {
            for (const track of stream.getTracks()) {
                track.stop();
            }
            return;
        }
        const [audio = undefined] = stream.getAudioTracks();
        const [video = undefined] = stream.getVideoTracks();
        this.displayTracks = {
            audio,
            video,
        };
        if (video) {
            video.applyConstraints({
                frameRate: {
                    min: Settings.videoEncoderDefaultFrameRate,
                },
            });
            const onVideoTrackEnd = () => {
                if (this.displayTracks.video === video) {
                    this.displayTracks.video = undefined;
                }
                if (!signal.aborted) {
                    this.engageDisplaySurface(undefined);
                }
                video.removeEventListener('ended', onVideoTrackEnd);
            };
            video.addEventListener('ended', onVideoTrackEnd);
        }
        if (audio) {
            const onEnd = () => {
                if (this.displayTracks.audio === audio) {
                    this.displayTracks.audio = undefined;
                }
                audio.removeEventListener('ended', onEnd);
            };
            audio.addEventListener('ended', onEnd);
        }
        this.displaySurface = value;
        this.emit('displaySurfaceChange', value);
        this.maybeUpdateMediaStream();
    }
    /**
     * Starts tracks associated with media devices identified by given device ids.
     */
    async engageDevices({ audio: microphoneDeviceId, video: cameraDeviceId, }) {
        if (this.engageDevicesAbortController) {
            this.engageDevicesAbortController.abort();
            this.engageDevicesAbortController = undefined;
        }
        const constraints = {
            audio: false,
            video: false,
        };
        function isTrackStale(track, deviceId) {
            return (!track ||
                track.readyState === 'ended' ||
                track.getSettings().deviceId !== deviceId);
        }
        const presence = await this.getDevicePresenceSnapshot();
        if (microphoneDeviceId &&
            this.permissions.audio &&
            presence.audio &&
            isTrackStale(this.userTracks.audio, microphoneDeviceId)) {
            constraints.audio = {
                deviceId: {
                    exact: microphoneDeviceId,
                },
            };
        }
        if (cameraDeviceId &&
            this.permissions.video &&
            presence.video &&
            isTrackStale(this.userTracks.video, cameraDeviceId)) {
            constraints.video = {
                ...DefaultVideoConstraints,
                deviceId: { exact: cameraDeviceId },
            };
        }
        if (!constraints.audio && !constraints.video) {
            return;
        }
        const abortController = new AbortController();
        this.engageDevicesAbortController = abortController;
        const { signal } = abortController;
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (signal.aborted) {
            for (const track of stream.getTracks()) {
                track.stop();
            }
            return;
        }
        const [audioTrack = undefined] = stream.getAudioTracks();
        const [videoTrack = undefined] = stream.getVideoTracks();
        if (constraints.audio) {
            this.userTracks.audio = audioTrack;
            if (audioTrack) {
                const onAudioTrackEnd = () => {
                    if (this.userTracks.audio === audioTrack) {
                        this.userTracks.audio = undefined;
                    }
                    audioTrack.removeEventListener('ended', onAudioTrackEnd);
                };
                audioTrack.addEventListener('ended', onAudioTrackEnd);
            }
        }
        if (constraints.video) {
            this.userTracks.video = videoTrack;
            if (videoTrack) {
                const onVideoTrackEnd = () => {
                    if (this.userTracks.video === videoTrack) {
                        this.userTracks.video = undefined;
                    }
                    videoTrack.removeEventListener('ended', onVideoTrackEnd);
                };
                videoTrack.addEventListener('ended', onVideoTrackEnd);
            }
        }
        this.maybeUpdateMediaStream();
    }
    /**
     * Swaps the media stream and triggers the `mediaStreamChange`. All only if necessary.
     */
    maybeUpdateMediaStream() {
        const [newAudioTrack, newVideoTrack] = [
            this.displayTracks.audio || this.userTracks.audio,
            this.displayTracks.video || this.userTracks.video,
        ];
        const [currentAudioTrack = undefined] = this.mediaStream.getAudioTracks();
        const [currentVideoTrack = undefined] = this.mediaStream.getVideoTracks();
        if (currentAudioTrack !== newAudioTrack ||
            currentVideoTrack !== newVideoTrack) {
            this._mediaStream = new MediaStream([newAudioTrack, newVideoTrack].filter(Boolean));
            this.emit('mediaStreamChange', this._mediaStream);
        }
    }
    /**
     * Video/audio pair representing current media device selection.
     */
    get selectedDevices() {
        const { selectedAudioDeviceId, selectedVideoDeviceId } = this;
        return {
            audio: selectedAudioDeviceId
                ? this._devices.audioById[selectedAudioDeviceId]
                : undefined,
            video: selectedVideoDeviceId
                ? this._devices.videoById[selectedVideoDeviceId]
                : undefined,
        };
    }
    /**
     * A collection of audio/video device lists.
     */
    get devices() {
        return {
            audio: this._devices.audio,
            video: this._devices.video,
        };
    }
    /**
     * Makes given device id the selected one for given media kind.
     */
    async selectDeviceById(kind, deviceId) {
        const previousSelectedDeviceIds = {
            audio: this.selectedAudioDeviceId,
            video: this.selectedVideoDeviceId,
        };
        if (kind === 'audio') {
            const device = this._devices.audioById[deviceId];
            if (device) {
                this._selectedAudioDeviceId = deviceId;
                safeStore('audioDeviceId', deviceId);
            }
        }
        else {
            const device = this._devices.videoById[deviceId];
            if (device) {
                this._selectedVideoDeviceId = deviceId;
                safeStore('videoDeviceId', deviceId);
            }
        }
        this.maybeEngageSelectedDevices(previousSelectedDeviceIds);
    }
}
